/**
 * Offline-grace bookkeeping for session revalidation.
 *
 * Every successful online validation stamps (account, time) in localStorage.
 * While offline, the app keeps working only within OFFLINE_GRACE_MS of the
 * last stamp; past that it blocks until Microsoft can be reached again. This
 * is what turns "IT disabled the account" into "the app stops working on
 * their machine" — see SessionGuard in AuthGate.tsx.
 */

export const OFFLINE_GRACE_MS = 48 * 60 * 60 * 1000; // 48 hours

const STAMP_KEY = 'bgd-auth-last-validated';

export interface ValidationStamp {
  /** MSAL homeAccountId the stamp belongs to. */
  accountId: string;
  /** Epoch ms of the last successful online validation. */
  at: number;
}

export function readStamp(): ValidationStamp | null {
  try {
    const raw = localStorage.getItem(STAMP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ValidationStamp;
    if (typeof parsed.accountId !== 'string' || typeof parsed.at !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeStamp(accountId: string, at: number = Date.now()): void {
  try {
    localStorage.setItem(STAMP_KEY, JSON.stringify({ accountId, at }));
  } catch {
    /* storage full/blocked — worst case the user revalidates online */
  }
}

export function clearStamp(): void {
  try {
    localStorage.removeItem(STAMP_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * True when the stamp belongs to this account and is fresh enough to allow
 * offline use. A stamp from a different account never counts (a machine
 * shared between users must revalidate per user).
 */
export function withinGrace(
  stamp: ValidationStamp | null,
  accountId: string,
  now: number = Date.now()
): boolean {
  if (!stamp || stamp.accountId !== accountId) return false;
  // A stamp from the future means clock tampering or a corrupt write — treat
  // as expired rather than granting unbounded offline use.
  if (stamp.at > now) return false;
  return now - stamp.at < OFFLINE_GRACE_MS;
}
